import { api } from './client';
import type { User, CoCompany } from './types';

// ── DTOs (mirror backend dto/access-manager.dto.ts) ────────────────────

export type PermissionScope = 'COMPANY' | 'AGENCY' | 'SHARED_COMPANY';

export interface PermissionAssignment {
  entity: string;
  action: string;
  scopeType?: PermissionScope;
  targetCompanyId?: number;
  appType?: string;
  expiresAt?: string;
}

export interface AccessRole {
  id: number;
  name: string;
  description?: string | null;
  type: string;
  /** Legacy single-company scope. New code reads `scopes` instead. */
  companyId: number | null;
  status: string;
  permissions?: Array<{
    id: number;
    entity: string;
    action: string;
    scopeType?: PermissionScope;
    targetCompanyId?: number;
    appType?: string;
  }>;
  company?: { id: number; name: string } | null;
  /** Multi-company scopes (junction). Empty → falls back to `companyId`. */
  scopes?: Array<{
    id: number;
    scopedCompanyId: number;
    scopedCompany?: { id: number; name: string };
  }>;
}

export interface CreateMemberInput {
  email: string;
  firstname: string;
  lastname: string;
  phone?: string;
  password?: string;
  companyId: number;
  roleId?: number;
  appType: string;
}

export interface UpdateMemberInput {
  firstname?: string;
  lastname?: string;
  phone?: string;
  roleId?: number;
  companyId?: number;
}

export interface CreateRoleInput {
  name: string;
  description?: string;
  type: string;
  /** Legacy single-company scope. */
  companyId?: number;
  /** Multi-company scope — preferred for new roles. */
  companyIds?: number[];
  permissions?: PermissionAssignment[];
}

export interface UpdateRoleInput {
  name?: string;
  description?: string;
  companyIds?: number[];
  permissions?: PermissionAssignment[];
}

export interface ExternalGrant {
  id: number;
  granteeUserId: number;
  sharedCompanyId: number;
  ownerCompanyId: number;
  appType?: string;
  grantedById: number;
  grantedAt: string;
  expiresAt?: string;
  revokedAt?: string;
  revokedById?: number;
  note?: string;
  status: string;
  grantee?: User;
  sharedCompany?: { id: number; name: string };
  ownerCompany?: { id: number; name: string };
  grantedBy?: User;
  mirroredPermissions?: Array<{
    id: number;
    entity: string;
    action: string;
    targetCompanyId?: number;
  }>;
}

export interface CreateGrantInput {
  granteeUserId?: number;
  granteeEmail?: string;
  sharedCompanyId: number;
  appType?: string;
  permissions: PermissionAssignment[];
  expiresAt?: string;
  note?: string;
}

export interface UpdateGrantInput {
  permissions?: PermissionAssignment[];
  expiresAt?: string;
  note?: string;
}

// ── Envelope helpers ────────────────────────────────────────────────────

interface Envelope<T> {
  data: { items: T[]; total: number };
}

// ── API ─────────────────────────────────────────────────────────────────

export const accessManagerApi = {
  // Members
  listMembers: (params?: { companyId?: number; appType?: string; range?: string }) =>
    api.get<Envelope<User>>('/access-manager/members', { params }),
  getMember: (id: number) =>
    api.get<User>(`/access-manager/members/${id}`),
  createMember: (body: CreateMemberInput) =>
    api.post<{ user: User; created: boolean }>('/access-manager/members', body),
  updateMember: (id: number, body: UpdateMemberInput) =>
    api.put<User>(`/access-manager/members/${id}`, body),
  deleteMember: (id: number) =>
    api.delete(`/access-manager/members/${id}`),
  /**
   * Set a member's password (manager-grade reset). Backend endpoint sits on
   * the `user` controller (`POST /user/:id/change-password`) — it requires
   * `User:edit` to enter and `User:full` (+ same company) to mutate someone
   * else's password.
   */
  setMemberPassword: (id: number, newPassword: string) =>
    api.post<{ success: boolean; message: string }>(
      `/user/${id}/change-password`,
      { newPassword },
    ),
  getEffectivePermissions: (id: number, appType?: string) =>
    api.get<{ data: { items: Array<{ entity: string; action: string; source: string; scopeType?: string; targetCompanyId?: number }>; total: number } }>(
      `/access-manager/members/${id}/effective-permissions`,
      { params: { appType } },
    ),

  // Roles
  listRoles: (params?: { companyId?: number; appType?: string }) =>
    api.get<Envelope<AccessRole>>('/access-manager/roles', { params }),
  getRole: (id: number) =>
    api.get<AccessRole>(`/access-manager/roles/${id}`),
  createRole: (body: CreateRoleInput) =>
    api.post<AccessRole>('/access-manager/roles', body),
  updateRole: (id: number, body: UpdateRoleInput) =>
    api.put<AccessRole>(`/access-manager/roles/${id}`, body),
  deleteRole: (id: number) =>
    api.delete(`/access-manager/roles/${id}`),

  // Permissions overrides (per-user direct grants, excludes grant-mirrored)
  listUserPermissions: (userId: number, appType?: string) =>
    api.get<{ data: { items: PermissionAssignment[]; total: number } }>(
      `/access-manager/members/${userId}/permissions`,
      { params: { appType } },
    ),
  setUserPermissions: (userId: number, permissions: PermissionAssignment[]) =>
    api.put<{ data: { items: PermissionAssignment[] }; success: boolean }>(
      `/access-manager/members/${userId}/permissions`,
      { permissions },
    ),
  revokeUserPermission: (userId: number, permissionId: number) =>
    api.delete(`/access-manager/members/${userId}/permissions/${permissionId}`),

  // External grants (cross-company sharing)
  listGrants: (direction: 'outgoing' | 'incoming' = 'outgoing', appType?: string) =>
    api.get<{ data: { items: ExternalGrant[]; total: number } }>(
      '/access-manager/grants',
      { params: { direction, appType } },
    ),
  getGrant: (id: number) =>
    api.get<ExternalGrant>(`/access-manager/grants/${id}`),
  createGrant: (body: CreateGrantInput) =>
    api.post<ExternalGrant>('/access-manager/grants', body),
  updateGrant: (id: number, body: UpdateGrantInput) =>
    api.put<ExternalGrant>(`/access-manager/grants/${id}`, body),
  revokeGrant: (id: number) =>
    api.delete(`/access-manager/grants/${id}`),

  // Manageable companies tree
  listManageableCompanies: () =>
    api.get<{ data: { items: CoCompany[]; total: number } }>(
      '/access-manager/companies/manageable',
    ),

  // Audit
  audit: (params?: { entity?: string; userId?: number; from?: string; to?: string; range?: string }) =>
    api.get<{ data: { items: Array<{ id: number; entity: string; action: string; userId: number; description?: string; createdAt: string }>; total: number } }>(
      '/access-manager/audit',
      { params },
    ),
  memberAccessHistory: (id: number) =>
    api.get<{
      data: {
        logs: Array<Record<string, unknown>>;
        grants: ExternalGrant[];
        permissions: PermissionAssignment[];
      };
    }>(`/access-manager/audit/members/${id}/access-history`),
};
